import { createMachine, interpret } from "xstate";
import Debug from "debug";
import StrangerBeg from "model/events/story/stranger/beg";
import StrangerDrunk from "model/events/story/stranger/drunk";
import StrangerKilled from "model/events/story/stranger/killed";
import BrothersDead from "model/events/story/brothers/dead";
import BrothersKill from "model/events/story/brothers/kill";
import BrothersHelp from "model/events/story/brothers/help";
import BrothersTrial from "model/events/story/brothers/trial";
import TomDead from "model/events/story/tom/dead";
import TomDying from "model/events/story/tom/dying";
import TomKill from "model/events/story/tom/kill";
import TomAndBrothers from "model/events/story/tom/tomAndBrothers";
import PartyFoundGood from "model/events/story/gang/partyFoundGood";
import enterEvent from "model/events/story/enter";
import placeholder from "model/events/story/placeholder";
import Admin from "admin";
const debug = Debug("model:events:story");

const isVillain = (context, { context: { party } }) => {
  const charisma = party.traits.getTraitValue("charisma");
  debug("isVillain", charisma);
  return charisma < 0;
};

const noAmmoOrLuck = (context, { context: { party } }) => {
  const {
    ammo: { value: ammo },
    luck: { value: luck },
  } = party.resources.attribs;
  debug("noAmmoOrLuck", ammo, luck);
  return ammo < 10 || luck < 5;
};

/**
 * Long Tom's predicates used to read `{ routeLength, river, sea }` straight off the game
 * context. Those fields never existed there — they came from the old util/move.js spread,
 * long deleted — so `river.isIn` threw a TypeError on every day change. The old
 * setTimeout dispatch in Calendar swallowed it, along with every listener behind it.
 *
 * The real fields, per GameContext's state shape:
 *   routeLength -> map.route.length
 *   river.isIn  -> terrain.context.inRiver
 *   sea.isIn    -> terrain.context.inWater
 */
const routeLength = ({ map }) => map?.route?.length ?? 0;

const onDryLand = ({ terrain }) => !terrain?.context?.inRiver && !terrain?.context?.inWater;

// null, not false, while the map is still generating — the caller returns early rather than
// letting a zero-length route read as "halfway there".
const pastHalfway = context => {
  const length = routeLength(context);
  if (!length) return null;
  return context.party.step >= Math.floor(length / 2);
};

const events = {
  BEG: {
    type: "BEG",
    isTriggered: () => false,
  },
  ROB: {
    type: "ROB",
    isTriggered: () => false,
  },
  WORK: {
    type: "WORK",
    isTriggered: () => false,
  },
  BROS_EARLY: {
    type: "BROS_EARLY",
    isTriggered: (daysElapsed, context) => {
      const { party, political } = context;
      return party.city === political.roadCities[1].name; //&& daysElapsed / 2 <= party.history.attribs.step.value
    },
  },
  BROS_LATE: {
    type: "BROS_LATE",
    isTriggered: (daysElapsed, { party, political }) => {
      return (
        party.history.attribs.step.value >= political.roadCities[1].step &&
        daysElapsed / 2 > party.history.attribs.step.value
      );
    },
  },
  TOM_EARLY: {
    type: "TOM_EARLY",
    isTriggered: (daysElapsed, context) => {
      const past = pastHalfway(context);
      if (past === null) return false;

      debug("TOM EARLY?", past, onDryLand(context), daysElapsed <= routeLength(context));
      return past && onDryLand(context) && daysElapsed <= routeLength(context);
    },
  },
  TOM_LATE: {
    type: "TOM_LATE",
    isTriggered: (daysElapsed, context) => {
      const past = pastHalfway(context);
      if (past === null) return false;

      debug("TOM Late?", past, onDryLand(context), daysElapsed > routeLength(context));
      return past && onDryLand(context) && daysElapsed > routeLength(context);
    },
  },
  EARLY: {
    type: "EARLY",
    isTriggered: (daysElapsed, { party, political }) => true,
  },
  LATE: {
    type: "LATE",
    isTriggered: (daysElapsed, { party, political }) => false,
  },
  END: {
    type: "END",
    isTriggered: (daysElapsed, { arrived }) => arrived,
  },
};

const states = {
  StrangerBeg,
  StrangerDrunk,
  StrangerKilled,
  BrothersDead,
  BrothersKill,
  BrothersHelp,
  BrothersTrial,
  TomDying,
  TomDead,
  TomKill,
  TomAndBrothers,
  PartyFoundGood,
  PartyFoundBad: placeholder("PartyFoundBad"),
  PartyNotFound: placeholder("PartyNotFound"),
  Death: placeholder("Death"),
  Victim: placeholder("Victim"),
  Ringleader: placeholder("Ringleader"),
  KillEveryone: placeholder("KillEveryone"),
};

const story = createMachine({
  id: "story",
  initial: Admin().storyInitial,
  predictableActionArguments: true,
  states: {
    idle: {
      on: {
        BEG: {
          target: "StrangerBeg",
        },
        ROB: {
          target: "StrangerKilled",
        },
        WORK: {
          target: "StrangerDrunk",
        },
      },
    },
    StrangerBeg: {
      on: {
        BROS_LATE: {
          target: "BrothersDead",
        },
        BROS_EARLY: {
          target: "BrothersTrial",
        },
      },
    },
    StrangerKilled: {
      on: {
        BROS_EARLY: {
          target: "BrothersKill",
        },
        BROS_LATE: {
          target: "BrothersDead",
        },
      },
    },
    StrangerDrunk: {
      on: {
        BROS_LATE: {
          target: "BrothersDead",
        },
        BROS_EARLY: {
          target: "BrothersHelp",
        },
      },
    },
    BrothersDead: {
      on: {
        TOM_EARLY: {
          target: "TomDying",
        },
        TOM_LATE: {
          target: "TomDead",
        },
      },
    },
    BrothersTrial: {
      on: {
        TOM_EARLY: {
          target: "TomDying",
        },
        TOM_LATE: {
          target: "TomDead",
        },
      },
    },
    BrothersKill: {
      on: {
        TOM_EARLY: {
          target: "TomKill",
        },
        TOM_LATE: {
          target: "TomDead",
        },
      },
    },
    BrothersHelp: {
      on: {
        TOM_EARLY: {
          target: "TomAndBrothers",
        },
        TOM_LATE: {
          target: "TomDead",
        },
      },
    },
    TomDying: {
      on: {
        EARLY: [
          {
            cond: isVillain,
            target: "PartyFoundBad",
          },
          {
            target: "PartyFoundGood",
          },
        ],
        LATE: {
          target: "PartyNotFound",
        },
      },
    },
    TomDead: {
      on: {
        EARLY: [
          {
            cond: isVillain,
            target: "PartyFoundBad",
          },
          {
            target: "PartyFoundGood",
          },
        ],
        LATE: {
          target: "PartyNotFound",
        },
      },
    },
    TomAndBrothers: {
      on: {
        LATE: {
          target: "PartyNotFound",
        },
        EARLY: {
          target: "PartyFoundGood",
        },
      },
    },
    TomKill: {
      on: {
        EARLY: {
          target: "PartyFoundBad",
        },
        LATE: {
          target: "PartyNotFound",
        },
      },
    },
    PartyNotFound: {
      type: "final",
    },
    PartyFoundGood: {
      on: {
        END: [
          {
            cond: noAmmoOrLuck,
            target: "Death",
          },
          {
            target: "Victim",
          },
        ],
      },
    },
    PartyFoundBad: {
      on: {
        END: [
          {
            cond: noAmmoOrLuck,
            target: "Death",
          },
          {
            cond: isVillain,
            target: "Ringleader",
          },
          {
            target: "KillEveryone",
          },
        ],
      },
    },
    KillEveryone: {
      type: "final",
    },
    Death: {
      type: "final",
    },
    Victim: {
      type: "final",
    },
    Ringleader: {
      type: "final",
    },
  },
});

// Machine instance with internal state
let service = interpret(story)
  .onTransition((state) => debug(state.value))
  .start();

let currentState = story.initialState;

const Story = (setEvent) => {
  const enter = enterEvent(setEvent);
  // const stranger = strangerEvent(setEvent);

  const reset = () => {
    currentState = story.initialState;
    // Stop the current service
    service.stop();
    // Start a new instance of the service
    service = interpret(story)
      .onTransition((state) => debug(state.value))
      .start();
  };

  const next = (setEvent, context, triggeredEvent) => {
    if (currentState.done) return debug("The End");

    let type = triggeredEvent ||
      currentState
        .nextEvents[Math.floor(Math.random() * currentState.nextEvents.length)];
    debug("type", type, currentState.nextEvents);

    currentState = service.send({ ...type, context });
    debug("currentState", currentState);
    if (!currentState.changed) {
      debug(type);
    }

    debug(
      "current state value",
      currentState.value,
      states[currentState.value],
    );
    return states[currentState.value](setEvent);
  };

  const setStage = (stage, setEvent, context) => {
    for (let i = 0; i < stage; i++) {
      if (i === 0) continue;
      next(setEvent, context);
    }
  };

  return {
    next,
    currentState,
    // The live branch the story is standing on. `currentState` above is captured when Story() is
    // called, and EventManager calls it once at module load, so that copy never moves — anything
    // that needs to know where the story actually is has to go through this.
    state: () => currentState.value,
    nextEvents: () =>
      currentState.nextEvents.map((event) => {
        const result = events[event];
        debug("result", result, event);
        return result;
      }),
    setStage,
    enter,
    reset,
  };
};

export default Story;
